import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, Shield, Clock, CreditCard, Users, Star, 
  CheckCircle2, Phone, TrendingUp, Zap, Heart, Award, Mail, Lock, UserCog, Download
} from "lucide-react";
import { LOGO_ICON_WHITE, LOGO_ICON_BLUE, LOGO_FULL_WHITE } from '@/components/SplashScreen';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    { 
      icon: Clock, 
      title: "Quick Approval", 
      desc: "Get approved within 24 hours with minimal documentation",
      color: "#00BCD4"
    },
    { 
      icon: Shield, 
      title: "100% Secure", 
      desc: "Bank-grade security for all your transactions",
      color: "#4CAF50"
    },
    { 
      icon: CreditCard, 
      title: "Flexible EMI", 
      desc: "Choose from 1, 2, or 3 month payment options",
      color: "#FF6B35"
    },
    { 
      icon: TrendingUp, 
      title: "Build Credit", 
      desc: "Improve your credit score with on-time payments",
      color: "#9C27B0"
    },
  ];

  const steps = [
    { num: "01", title: "Register", desc: "Sign up with your mobile number and complete KYC" },
    { num: "02", title: "Apply", desc: "Enter your EMI details and required amount" },
    { num: "03", title: "Get Approved", desc: "Quick verification and instant approval" },
    { num: "04", title: "Receive Funds", desc: "Amount transferred directly to your bank" },
  ];

  const testimonials = [
    { name: "Rahul M.", rating: 5, text: "Rebuilt saved me during a financial emergency. Quick approval and transparent process!", avatar: "RM" },
    { name: "Priya S.", rating: 5, text: "Finally a service that understands the middle class. Highly recommended!", avatar: "PS" },
    { name: "Amit K.", rating: 5, text: "The app is so easy to use. Got my EMI assistance in just 2 days.", avatar: "AK" },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex flex-col overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0A2540 0%, #0D47A1 50%, #1565C0 100%)'
        }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full opacity-5"
              style={{
                width: Math.random() * 300 + 100,
                height: Math.random() * 300 + 100,
                background: 'linear-gradient(135deg, #00BCD4, #1565C0)',
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `logoFloat ${5 + Math.random() * 5}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        {/* Navigation */}
        <nav className="relative z-10 px-6 py-5">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_ICON_WHITE} alt="Rebuilt" className="h-10 w-10" />
              <span 
                className="text-xl font-bold text-white tracking-tight"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                REBUILT
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-white/70 hover:text-white transition-colors">Features</a>
              <a href="#how-it-works" className="text-white/70 hover:text-white transition-colors">How It Works</a>
              <a href="#testimonials" className="text-white/70 hover:text-white transition-colors">Reviews</a>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/agent-register')}
                className="hidden md:block px-4 py-2 text-white/80 hover:text-white transition-colors text-sm font-medium"
              >
                Become an Agent
              </button>
              <button
                onClick={() => navigate('/agent/login')}
                className="hidden md:block px-4 py-2 text-white/80 hover:text-white transition-colors text-sm font-medium"
              >
                Agent Login
              </button>
              <button
                onClick={() => navigate('/customer/login')}
                data-testid="customer-login-nav"
                className="px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #00BCD4 0%, #4DD0E1 100%)',
                  color: '#0A2540'
                }}
              >
                Get Started
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center px-6 py-12">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-3xl">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-cyan-300 text-sm font-medium mb-6 animate-fade-in">
                <Zap className="h-4 w-4" />
                India's Trusted EMI Partner
              </div>

              {/* Main Heading */}
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 animate-fade-in-up"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                Rebuild Your
                <span 
                  className="block"
                  style={{
                    background: 'linear-gradient(135deg, #00BCD4 0%, #4DD0E1 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Financial Future
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-lg sm:text-xl text-white/70 mb-8 max-w-xl animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Get instant EMI assistance up to ₹50,000. Quick approval, flexible repayment, and zero hidden charges.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <button
                  onClick={() => navigate('/customer/login')}
                  data-testid="customer-login-hero"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  style={{
                    background: 'linear-gradient(135deg, #00BCD4 0%, #4DD0E1 100%)',
                    color: '#0A2540'
                  }}
                >
                  Get Started Free
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => navigate('/download')}
                  data-testid="download-app-hero"
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white hover:bg-white/20"
                >
                  <Download className="h-5 w-5" />
                  Download App
                </button>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 flex flex-wrap items-center gap-8 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {['R', 'P', 'A', 'S'].map((letter, i) => (
                      <div 
                        key={i} 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 border-white/20"
                        style={{ 
                          background: `hsl(${180 + i * 30}, 70%, 50%)`,
                          color: 'white'
                        }}
                      >
                        {letter}
                      </div>
                    ))}
                  </div>
                  <span className="text-white/70 text-sm">100+ Happy Customers</span>
                </div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-white/70 text-sm ml-2">4.9/5 Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="relative z-10">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-50 text-cyan-600 text-sm font-semibold mb-4">
              <Award className="h-4 w-4" />
              Why Choose Us
            </span>
            <h2 
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Built for Your Peace of Mind
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We understand financial emergencies. That's why we've made EMI assistance simple, fast, and transparent.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="group p-6 rounded-2xl bg-white border border-gray-100 hover:border-transparent hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ 
                    background: `${feature.color}15`,
                    color: feature.color
                  }}
                >
                  <feature.icon className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-4">
              <Zap className="h-4 w-4" />
              Simple Process
            </span>
            <h2 
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Get Funds in 4 Easy Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-cyan-400 to-transparent -z-10" />
                )}
                <div className="text-center">
                  <div 
                    className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white"
                    style={{
                      background: 'linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)'
                    }}
                  >
                    {step.num}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-50 text-pink-600 text-sm font-semibold mb-4">
              <Heart className="h-4 w-4" />
              Customer Love
            </span>
            <h2 
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Trusted by Thousands
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div 
                key={i}
                className="p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                    style={{ background: 'linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)' }}
                  >
                    {t.avatar}
                  </div>
                  <span className="font-semibold text-gray-900">{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'linear-gradient(135deg, #0A2540 0%, #0D47A1 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Ready to Rebuild Your Finances?
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Join 100+ customers who trust Rebuilt for their EMI needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/customer/login')}
              data-testid="customer-login-footer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
              style={{
                background: 'linear-gradient(135deg, #00BCD4 0%, #4DD0E1 100%)',
                color: '#0A2540'
              }}
            >
              <Users className="h-5 w-5" />
              Start Your Journey
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_ICON_WHITE} alt="Rebuilt" className="h-10 w-10" />
                <span className="text-xl font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>REBUILT</span>
              </div>
              <p className="text-gray-400 mb-4">Your trusted EMI partner for financial emergencies.</p>
              <div className="flex items-center gap-4">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span className="text-sm text-gray-400">Verified & Trusted Platform</span>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Reviews</a></li>
                <li>
                  <button
                    onClick={() => navigate('/download')}
                    data-testid="footer-download-app"
                    className="inline-flex items-center gap-2 hover:text-white transition-colors text-left"
                  >
                    <Download className="h-4 w-4 text-cyan-400" />
                    Download App
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal & Policies */}
            <div>
              <h4 className="font-semibold mb-4">Legal & Policies</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/policies/terms-of-service')} className="hover:text-white transition-colors text-left">Terms of Service</button></li>
                <li><button onClick={() => navigate('/policies/privacy-policy')} className="hover:text-white transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/policies/refund-policy')} className="hover:text-white transition-colors text-left">Refund Policy</button></li>
                <li><button onClick={() => navigate('/policies/payment-gateway-policy')} className="hover:text-white transition-colors text-left">Payment Policy</button></li>
                <li><button onClick={() => navigate('/policies/grievance-policy')} className="hover:text-white transition-colors text-left">Grievance Redressal</button></li>
                <li><button onClick={() => navigate('/delete-account')} className="hover:text-white transition-colors text-left" data-testid="footer-delete-account">Delete Account</button></li>
                <li><button onClick={() => navigate('/delete-data')} className="hover:text-white transition-colors text-left" data-testid="footer-delete-data">Delete Data</button></li>
              </ul>
            </div>

            {/* Access */}
            <div>
              <h4 className="font-semibold mb-4">Access</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => navigate('/customer/login')} className="hover:text-white transition-colors">Customer Login</button></li>
                <li><button onClick={() => navigate('/agent/login')} className="hover:text-white transition-colors">Agent Login</button></li>
                <li><button onClick={() => navigate('/agent-register')} className="hover:text-white transition-colors">Become an Agent</button></li>
                <li><button onClick={() => navigate('/employee-login')} className="hover:text-white transition-colors text-xs opacity-70" data-testid="footer-employee-login">Employee Login</button></li>
              </ul>
              <div className="mt-6">
                <h4 className="font-semibold mb-3">Customer Care</h4>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <a href="tel:7666020197" className="hover:text-white transition-colors">7666 020 197</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-400 flex-shrink-0" />
                    <a href="mailto:support@rebuiltindia.co" className="hover:text-white transition-colors">support@rebuiltindia.co</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <span className="text-gray-500">© 2025 Rebuilt. All rights reserved.</span>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <button
                type="button"
                onClick={() => navigate('/admin-portal')}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 hover:text-amber-100 transition-colors"
                data-testid="admin-portal-link"
                aria-label="Admin Portal Login"
              >
                <Lock className="h-3.5 w-3.5" />
                <span className="font-medium">Admin Login</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/employee-login')}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-blue-500/30 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20 hover:text-blue-100 transition-colors"
                data-testid="employee-portal-link"
                aria-label="Employee Login"
              >
                <UserCog className="h-3.5 w-3.5" />
                <span className="font-medium">Employee Login</span>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
